import Dexie from 'dexie';
import { normalizeJourneyData } from './actionItemModel';

const db = new Dexie('JourneyWorkingCopyStore');

db.version(1).stores({
  journeys: '&id, cloudDocumentId, updatedAt, lastLocalSaveAt, lastCloudSaveAt, source',
});

const nowIso = () => new Date().toISOString();

const normalizeLocalJourneyRecord = (record) => record
  ? {
    ...record,
    data: normalizeJourneyData(record.data),
  }
  : record;

export const makeLocalJourneyId = (userId, journeyId = 'default') => (
  `${userId || 'anonymous'}:${journeyId || 'default'}`
);

export const createLocalJourneyRecord = ({
  userId,
  journeyId = 'default',
  title = 'My Journey',
  data,
  cloudDocumentId = null,
  source = 'local',
  lastCloudSaveAt = null,
}) => {
  const timestamp = nowIso();
  return {
    id: makeLocalJourneyId(userId, journeyId),
    title,
    version: 1,
    createdAt: timestamp,
    updatedAt: timestamp,
    lastLocalSaveAt: timestamp,
    lastCloudSaveAt,
    cloudDocumentId,
    source,
    data: normalizeJourneyData(data),
  };
};

export const getLocalJourney = async (userId, journeyId = 'default') => {
  return normalizeLocalJourneyRecord(await db.journeys.get(makeLocalJourneyId(userId, journeyId)));
};

export const listLocalJourneys = async (userId) => {
  const ownerPrefix = `${userId || 'anonymous'}:`;
  const records = await db.journeys.where('id').startsWith(ownerPrefix).toArray();
  return records.map(normalizeLocalJourneyRecord);
};

export const deleteLocalJourney = async (userId, journeyId = 'default') => {
  await db.journeys.delete(makeLocalJourneyId(userId, journeyId));
};

export const saveLocalJourney = async (record) => {
  const existing = await db.journeys.get(record.id);
  const timestamp = nowIso();
  const nextRecord = {
    ...record,
    createdAt: record.createdAt || existing?.createdAt || timestamp,
    updatedAt: timestamp,
    lastLocalSaveAt: timestamp,
    data: normalizeJourneyData(record.data),
  };

  await db.journeys.put(nextRecord);
  return nextRecord;
};

export const duplicateLocalJourney = async ({
  sourceRecord,
  userId,
  journeyId = 'default',
  title,
  source = 'local',
  cloudDocumentId = null,
  lastCloudSaveAt = null,
}) => {
  const record = createLocalJourneyRecord({
    userId,
    journeyId,
    title: title || sourceRecord?.title || 'My Journey',
    data: sourceRecord?.data,
    cloudDocumentId,
    source,
    lastCloudSaveAt,
  });

  await db.journeys.put(record);
  return record;
};

export const importFirestoreJourneyToLocal = async ({
  userId,
  journeyId = 'default',
  firestoreDoc,
}) => {
  const data = normalizeJourneyData(firestoreDoc?.graph ? JSON.parse(firestoreDoc.graph) : firestoreDoc);
  const record = createLocalJourneyRecord({
    userId,
    journeyId,
    title: firestoreDoc?.shortname || 'Firestore Journey',
    data,
    cloudDocumentId: firestoreDoc?.journeyId || journeyId,
    source: 'firestore-imported',
    lastCloudSaveAt: firestoreDoc?.updatedAt || null,
  });

  await db.journeys.put(record);
  return record;
};

export const replaceLocalJourneyData = async ({
  userId,
  journeyId = 'default',
  data,
  title = 'Imported Journey',
  cloudDocumentId = null,
  source = 'local',
}) => {
  const existing = await getLocalJourney(userId, journeyId);
  const base = existing || createLocalJourneyRecord({
    userId,
    journeyId,
    title,
    data,
    cloudDocumentId,
    source,
  });

  return saveLocalJourney({
    ...base,
    title,
    cloudDocumentId: cloudDocumentId ?? base.cloudDocumentId ?? null,
    source,
    data,
  });
};
